import React from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import Seo1 from './Seo1';
import Seo2 from './Seo2';
import Home from '../Home';


// --- Animations ---
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const gradientShift = keyframes`
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 5px 15px rgba(0, 123, 255, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 8px 25px rgba(0, 123, 255, 0.6);
  }
`;

// --- Global Styles ---
const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: #2d3748;
    line-height: 1.7;
    overflow-x: hidden;
  }
  * {
    box-sizing: border-box;
  }
  
  html {
    scroll-behavior: smooth;
  }
`;

// --- Styled Components for the Layout ---

const PageContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
  background: white;
  box-shadow: 0 0 50px rgba(0, 0, 0, 0.1);
  border-radius: 0 0 20px 20px;
  overflow: hidden;
`;

// --- Header Section Styles ---

const HeaderSection = styled.header`
  position: relative;
  background: linear-gradient(135deg, #1a202c 0%, #2d3748 100%);
  min-height: 600px;
  padding: 100px 5% 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.15) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.2) 0%, transparent 50%);
    animation: ${float} 6s ease-in-out infinite;
  }

  @media (max-width: 992px) {
    flex-direction: column;
    text-align: center;
    min-height: auto;
    padding: 120px 5% 60px;
  }
`;

const HeaderContent = styled.div`
  position: relative;
  z-index: 10;
  max-width: 800px;
  animation: ${fadeInUp} 0.8s ease-out;

  @media (max-width: 1200px) {
    max-width: 100%;
  }
`;

const MainCtaButton = styled.a`
  position: absolute;
  top: 300px;
  right: 5%;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
  padding: 16px 35px;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 700;
  font-size: 16px;
  white-space: nowrap;
  transition: all 0.3s ease;
  
  box-shadow: 0 5px 15px rgba(0, 123, 255, 0.4);
  animation: ${pulse} 2s ease-in-out infinite;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 123, 255, 0.6);
    animation: none;
  }

  @media (max-width: 992px) {
    position: static;
    margin-top: 30px;
    display: inline-block;
  }
`;

const Headline = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1.1;
  color: white;
  margin-bottom: 15px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const CustomSeoText = styled.span`
  background: linear-gradient(135deg, #ffd89b 0%, #19547b 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: block;
  margin-bottom: 10px;
`;

const BestCompanyText = styled.span`
  color: white;
  display: block;
`;

const Subtitle = styled.p`
  font-size: 1.4rem;
  color: #cbd5e0;
  font-weight: 300;
  margin-top: 10px;
  margin-bottom: 30px;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ServiceDescription = styled.p`
  font-size: 1.1rem;
  color: #e2e8f0;
  margin-bottom: 40px;
  line-height: 1.8;
  animation: ${fadeInUp} 0.8s ease-out 0.4s both;
`;

const SecondaryCtaButton = styled.a`
  background: linear-gradient(135deg, #e9a341 0%, #d88b0a 100%);
  color: #2d3748;
  padding: 16px 35px;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 700;
  font-size: 16px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 5px 15px rgba(233, 163, 65, 0.4);
  animation: ${fadeInUp} 0.8s ease-out 0.6s both;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(233, 163, 65, 0.6);
    
    &::before {
      left: 100%;
    }
  }

  &::after {
    content: '🚀';
    font-size: 1.2em;
  }
`;

// --- Middle Divider Section Styles ---

const MiddleDivider = styled.div`
  background: linear-gradient(135deg, #ffd89b 0%, #19547b 100%);
  padding: 40px 5%;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" fill="rgba(255,255,255,0.1)"><polygon points="0,0 1000,100 1000,0"></polygon></svg>');
    background-size: cover;
  }
`;

const DividerHeadline = styled.h2`
  color: #2d3748;
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  position: relative;
  animation: ${fadeInUp} 0.8s ease-out;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

// --- Card Section Styles ---

const CardSection = styled.section`
  padding: 80px 5%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 30px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
    padding: 60px 5%;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px 30px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f5f9;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.8s ease-out;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);

    &::before {
      transform: scaleX(1);
    }
  }

  &:nth-child(2)::before {
    background: linear-gradient(135deg, #e9a341 0%, #d88b0a 100%);
  }

  &:nth-child(3)::before {
    background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  }
`;

const CardTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 25px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  ${Card}:nth-child(2) & {
    background: linear-gradient(135deg, #e9a341 0%, #d88b0a 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  ${Card}:nth-child(3) & {
    background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const CardIconPlaceholder = styled.div`
  font-size: 3.5rem;
  margin-bottom: 25px;
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: ${float} 3s ease-in-out infinite;
`;

const CardBlock = styled.div`
  background: linear-gradient(135deg, #2d3748 0%, #4a5568 100%);
  color: white;
  padding: 15px 20px;
  margin-bottom: 25px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 1rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const CardListItem = styled.li`
  list-style: none;
  text-align: left;
  font-size: 1rem;
  color: #4a5568;
  margin-bottom: 15px;
  position: relative;
  padding-left: 30px;
  line-height: 1.6;
  transition: color 0.3s ease;

  ${Card}:hover & {
    color: #2d3748;
  }

  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    top: 0;
    width: 22px;
    height: 22px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
    transition: all 0.3s ease;
  }

  ${Card}:nth-child(2) &:before {
    background: linear-gradient(135deg, #e9a341 0%, #d88b0a 100%);
  }

  ${Card}:nth-child(3) &:before {
    background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  }
`;

const CardList = styled.ul`
  padding: 0;
  margin: 0;
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 0.9rem;
  opacity: 0.8;
  animation: ${float} 2s ease-in-out infinite;
  
  &::after {
    content: '↓';
    display: block;
    font-size: 1.5rem;
    margin-top: 5px;
  }

  @media (max-width: 992px) {
    display: none;
  }
`;

// --- Main React Component ---

const SEOforSmallBusiness = () => {
  return (
    <Home>
      <GlobalStyle />
      <PageContainer>
        {/* --- Header Section --- */}
        <HeaderSection>
          <HeaderContent>
            <Headline>
              <CustomSeoText>Custom SEO Solutions</CustomSeoText>
              <BestCompanyText>Best SEO Company in Patna</BestCompanyText>
            </Headline>
            <Subtitle>
              Premium SEO Services That Deliver Real Results
            </Subtitle>
            <ServiceDescription>
              Are you searching for the best SEO company in Patna? Don't miss out on valuable leads by
              neglecting search engine optimization (SEO). It's not just an option anymore.
              It's essential for expanding your business. Custom SEO solutions for websites and
              businesses are paramount to success. Whether you are selling products or services,
              including qualified leads, or boosting sales, a robust SEO strategy is a must-have.
            </ServiceDescription>
            <SecondaryCtaButton href="#contact">
              GET STARTED TODAY
            </SecondaryCtaButton>
          </HeaderContent>
          <MainCtaButton href="#contact">
            🚀 Need SEO Help?
          </MainCtaButton>
          <ScrollIndicator>Scroll to Explore</ScrollIndicator>
        </HeaderSection>

        {/* --- Middle Divider --- */}
        <MiddleDivider>
          <DividerHeadline>
            Why Do You Need Custom SEO for Your Website?
          </DividerHeadline>
        </MiddleDivider>

        {/* --- Card Section --- */}
        <CardSection>
          {/* Card 1: Online presence */}
          <Card>
            <CardTitle>Online Presence</CardTitle>
            <CardIconPlaceholder>🌐</CardIconPlaceholder>
            <CardBlock>
              Your website isn't on the first page
            </CardBlock>
            <CardList>
              <CardListItem>Search ranking needs improvement</CardListItem>
              <CardListItem>Limited visibility affects reach</CardListItem>
              <CardListItem>Struggling to stand out online</CardListItem>
              <CardListItem>Social media engagement is lacking</CardListItem>
              <CardListItem>Competitors outshining your presence</CardListItem>
            </CardList>
          </Card>

          {/* Card 2: Website traffic */}
          <Card>
            <CardTitle>Website Traffic</CardTitle>
            <CardIconPlaceholder>📉</CardIconPlaceholder>
            <CardBlock>
              Your website traffic has diminished
            </CardBlock>
            <CardList>
              <CardListItem>Decreased website visits noticed</CardListItem>
              <CardListItem>Traffic drop impacting engagement</CardListItem>
              <CardListItem>Need strategies for boosting traffic</CardListItem>
              <CardListItem>Analytics show declining visitors</CardListItem>
              <CardListItem>Conversion rate affected by traffic</CardListItem>
            </CardList>
          </Card>

          {/* Card 3: Leads declining */}
          <Card>
            <CardTitle>Leads Declining</CardTitle>
            <CardIconPlaceholder>🏪</CardIconPlaceholder>
            <CardBlock>
              Volume of leads has declined
            </CardBlock>
            <CardList>
              <CardListItem>Fewer inquiries coming in</CardListItem>
              <CardListItem>Lead generation efforts not delivering</CardListItem>
              <CardListItem>Conversion rate showing decline</CardListItem>
              <CardListItem>Decreasing potential customers</CardListItem>
              <CardListItem>Demand for services dropping</CardListItem>
            </CardList>
          </Card>
        </CardSection>
        
        <Seo1/>
        <Seo2/>
      </PageContainer>
    </Home>
  );
};

export default SEOforSmallBusiness;